import { Check } from "phosphor-react";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import ReportsApi from "~/admin/apis/reports.api";
import Card from "~/admin/components/Card";
import PageDataLoader from "~/admin/components/PageDataLoader";
import Pagination from "~/admin/components/Pagination";
import Table from "~/admin/components/Table";
import Td from "~/admin/components/Td";
import Th from "~/admin/components/Th";
import Tr from "~/admin/components/Tr";
import useDataLoading from "~/admin/hooks/useDataLoading";
import moment from "moment";
import useMount from "~/hooks/useMount";
import useLayout from "~/hooks/useLayout";
import UsersApi from "~/admin/apis/users.api";
import Badge from "~/admin/components/Badge";
import ConfirmationButton from "~/admin/components/ConfirmationButton";
import useLoader from "~/hooks/useLoader";
import Notifications from "~/admin/services/Notifications";

export default function ReportsPage() {
  const { setLayout } = useLayout();
  const params = useParams();
  const dataLoading = useDataLoading();
  const [reports, setReports] = useState([]);
  const [user, setUser] = useState(null);
  const loader = useLoader();

  useMount(() => {
    setLayout({
      title: "Signalements",
      activeMenuKeys: ["reports"],
    });
    loadData();
  });

  const loadData = async () => {
    dataLoading.start();
    setReports([]);
    setUser(null);

    let error = null;
    const data = await Promise.all([
      ReportsApi.paginateUserReports(params.userId),
      UsersApi.get(params.userId),
    ]).catch((e) => {
      error = e;
      return null;
    });

    dataLoading.stop(error);
    if (data) {
      setReports(data[0]);
      setUser(data[1]);
    }
  };

  const search = async (page = 1) => {
    let reports = await ReportsApi.paginateUserReports(
      params.userId,
      page
    ).catch((e) => {
      return null;
    });

    if (reports) {
      setReports(reports);
    }
  };

  const onPaginate = (page) => {
    search(page);
  };

  const block = async () => {
    loader.show();
    let user = await UsersApi.block(params.userId).catch((e) => {
      Notifications.error("Erreur lors du bloquage de l'utilisateur");
      return null;
    });

    loader.hide();

    if (user) {
      Notifications.success("Utilisateur bloqué avec succès");
      setUser(user);
    }
  };

  const unblock = async () => {
    loader.show();
    let user = await UsersApi.unblock(params.userId).catch((e) => {
      Notifications.error("Erreur lors du débloquage de l'utilisateur");
      return null;
    });

    loader.hide();

    if (user) {
      Notifications.success("Utilisateur débloqué avec succès");
      setUser(user);
    }
  };

  return (
    <PageDataLoader isLoading={dataLoading.isLoading}>
      <div className="w-full h-screen text-white flex flex-col">
        <div className="w-full h-[60px] border-b-2 border-slate-700 flex justify-end items-center px-10">
          {user && (
            <>
              {user.type === "influencer" ? (
                <Badge color="light">Influenceur</Badge>
              ) : (
                <Badge color="info">Entreprise</Badge>
              )}
              <div className="font-bold text-2xl ml-4">{user.name}</div>
            </>
          )}
        </div>
        <div className="p-6 overflow-auto">
          {reports.items?.length > 0 ? (
            <Card>
              <div className="w-fit pb-4">
                {user.blocked ? (
                  <ConfirmationButton
                    confirmationTitle="Débloquer l'utilisateur"
                    confirmationBody={`Êtes-vous sur de vouloir débloquer l'utilisateur ${user.name}`}
                    data={user.id}
                    onConfirm={unblock}
                  >
                    Débloquer l'utilisateur
                  </ConfirmationButton>
                ) : (
                  <ConfirmationButton
                    color="danger"
                    confirmationTitle="Bloquer l'utilisateur"
                    confirmationBody={`Êtes-vous sur de vouloir bloquer l'utilisateur ${user.name}`}
                    data={user.id}
                    onConfirm={block}
                  >
                    Bloquer l'utilisateur
                  </ConfirmationButton>
                )}
              </div>
              <Table>
                <thead>
                  <Tr>
                    <Th>Raison</Th>
                    <Th adaptToContent>Date</Th>
                  </Tr>
                </thead>
                <tbody>
                  {reports.items.map((report) => {
                    return (
                      <Tr key={report.id}>
                        <Td>{report.reportReason}</Td>
                        <Td>{moment(report.createdAt).format("DD/MM/YYYY")}</Td>
                      </Tr>
                    );
                  })}
                </tbody>
              </Table>
              {reports.items && (
                <div className="pt-4 text-center">
                  <Pagination
                    lastPage={reports.lastPage}
                    currentPage={reports.currentPage}
                    onPaginate={onPaginate}
                  />
                </div>
              )}
            </Card>
          ) : (
            <div className="px-4 py-2 bg-green-300 text-green-700 w-fit rounded-md mx-auto flex space-x-4 items-center">
              <div className="rounded-full border w-fit border-green-700 h-fit">
                <Check />
              </div>
              <div>Cet utilisateur n'a pas de signalement !</div>
            </div>
          )}
        </div>
      </div>
    </PageDataLoader>
  );
}
